/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import {
  FormProvider,
  useController,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as Inputs from "../common/hook-form/index";
import { SpinnerBlock } from "../common/Spinner";
import _ from "lodash";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { FormRow } from "../common/hook-form/index";
import { PrimaryButton } from "../../components/Buttons";
import {
  useArchiveVoucherType,
  useCreateVoucher,
  useGetCountries,
  useGetVoucher,
  useUpdateVoucher,
  useUploadVoucherImage,
} from "../../service/api";
import { routes } from "../../appRoutingHelpers";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  VoucherFormType,
  VoucherProduct,
} from "./models/VoucherFormSuccessResponse";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../components/ActionSpinner";
import {
  PriceInput,
  transformPriceInput,
  validateAboveOrEqualToZero,
} from "../../components/forms/inputs/PriceInput";
import {
  getCollectionMethodLabel,
  getCountrySpecificValues,
  transformPriceFromAPI,
} from "../../util";
import { css } from "@emotion/react";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { getCountryInfo } from "../../service/countries-service";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { ButtonAction } from "../../components/NewButtons";
import { DatePickerInput } from "../../components/forms/inputs/DatePickerInput";
import { SelectProductsSelector } from "../../components/SelectProductsSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SwitchInput } from "../../components/forms/inputs/SwitchInput";
import { CollectionMethod } from "../shop/model/ShopSuccessResponse";
import { CheckboxField } from "../../components/forms/fields/CheckboxField";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { TableBody } from "../../components/table-components/TableBody";
import { IconButton } from "../../components/IconButton";
import { TableRow } from "../../components/table-components/TableRow";
import { TableCellText } from "../../components/table-components/TableCellText";
import { SelectField } from "../../components/forms/fields/SelectField";

const additionalStylePriceInput = css`
  margin-bottom: 16px;
`;

const productsTableWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const tableWrapperStyle = css`
  border: 1px solid ${colors.greyBorder};
  border-radius: 6px;
  overflow: hidden;
`;

const removeProductButtonStyle = css`
  display: flex;
  justify-content: end;
  align-items: center;
`;

interface Props {}

const VOUCHER_TYPES = {
  PERCENTAGE_PRODUCT: "DISCOUNT_TYPE_PERCENTAGE_PRODUCT",
  PERCENTAGE_ORDER: "DISCOUNT_TYPE_PERCENTAGE_ORDER",
  VALUE_ORDER: "DISCOUNT_TYPE_VALUE_ORDER",
};

const voucherSchema = yup.object().shape({
  name: yup.string().required("Enter voucher name to continue"),
  title: yup.string().required("Enter voucher admin name to continue"),
  description: yup.string().required("Describe the voucher to continue"),
  duration: yup.string().required("Choose voucher validity to continue"),
  supportedRedemptionMethods: yup
    .array()
    .of(yup.string())
    .min(1, "Choose at least one redemption method"),
  voucherTypeDiscount: yup.object().shape({
    value: yup
      .string()
      .when("type", (type) => {
        if (
          type === VOUCHER_TYPES.PERCENTAGE_ORDER ||
          type === VOUCHER_TYPES.PERCENTAGE_PRODUCT
        ) {
          return yup
            .number()
            .typeError("Discount must be a number")
            .min(0, "Enter a value between 0 and 100 for percentage values.")
            .max(100, "Enter a value between 0 and 100 for percentage values.");
        }
        return yup
          .string()
          .nullable()
          .test("Testing if above zero", "Must be at least 0", (value, ctx) => {
            if (!value) {
              return true;
            }
            // Hardcoding DKK does not matter as we are only checking if it is above zero. Will get currency from API.
            return validateAboveOrEqualToZero(value, "DKK");
          });
      })
      .required("Enter discount value to continue"),
    type: yup.string().required(),
    appliesToProducts: yup
      .array()
      .of(
        yup.object().shape({
          id: yup
            .string()
            .required(
              "No product is chosen in this field. Add product or remove the field."
            ),
        })
      )
      .when("type", {
        is: VOUCHER_TYPES.PERCENTAGE_PRODUCT,
        then: yup.array().min(1, "Choose a minimum of one product"),
      }),
  }),
});

const VoucherTypeUpsert: React.FC<Props> = (props) => {
  const { countryCode, currency: defaultCurrency } = getCountrySpecificValues();

  const formMethods = useForm<VoucherFormType>({
    defaultValues: {
      countryCode,
      allowChallengeProgress: true,
      supportedRedemptionMethods: ["order"],
      limitOrderTypes: false,
      voucherTypeDiscount: {
        appliesToClassifications: [],
        appliesToCollectionMethods: [],
        appliesToProducts: [],
        type: VOUCHER_TYPES.PERCENTAGE_ORDER,
      },
    } as any,
    resolver: yupResolver(voucherSchema),
  });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [image, setImage] = React.useState<File | undefined>();
  const {
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors: formErrors },
    setValue,
    control,
  } = formMethods;

  const { replace, fields, remove } = useFieldArray({
    control,
    name: "voucherTypeDiscount.appliesToProducts",
  });
  const organization =
    useSelector(authSelectors.selectedOrganizationKeyName) || "";

  const { field } = useController({
    control,
    name: "voucherTypeDiscount.appliesToCollectionMethods",
  });

  const {
    mutateAsync: archiveVoucherType,
    isLoading: isLoadingArchiveVoucherType,
  } = useArchiveVoucherType();

  const {
    data: countries,
    isLoading: isLoadingCountries,
    error: listCountriesError,
  } = useGetCountries();

  const {
    data: voucherType,
    isLoading: isLoadingVoucherType,
    error: listVoucherTypeError,
  } = useGetVoucher(organization, id);

  const {
    mutateAsync: createVoucher,
    isLoading: isCreatingVoucher,
  } = useCreateVoucher();

  const {
    mutateAsync: updateVoucher,
    isLoading: isUpdatingVoucher,
  } = useUpdateVoucher(id);

  const {
    mutateAsync: uploadVoucherImage,
    isLoading: isUploadingVoucherImage,
  } = useUploadVoucherImage(id);

  const {
    field: { ref, value, onChange: controllerOnChange, ...controllerRest },
  } = useController({
    name: "supportedRedemptionMethods",
    control: formMethods.control,
  });

  const countryOptions = (countries || [])
    .filter((c) => c.enabled)
    .map((c) => ({
      label: c.englishName,
      value: c.countryCode,
    }));
  const isEdit = !!id;

  const currency =
    isEdit && voucherType
      ? voucherType.monetaryCurrency
      : (countries || []).find((c) => c.countryCode === watch("countryCode"))
          ?.currency ?? defaultCurrency;

  const isLoadingMutation =
    isCreatingVoucher || isUpdatingVoucher || isUploadingVoucherImage;

  React.useEffect(() => {
    if (isLoadingVoucherType || isLoadingCountries) {
      return;
    }

    if (isEdit && voucherType) {
      reset({
        id: voucherType.id,
        name: voucherType.name,
        title: voucherType.title,
        description: voucherType.description,
        duration: voucherType.duration,
        allowChallengeProgress: voucherType.allowChallengeProgress,
        countryCode: voucherType.countryCode,
        onlineposCampaignId: voucherType.onlineposCampaignId,
        expiresAt: voucherType.expiresAt || "",
        supportedRedemptionMethods:
          voucherType.supportedRedemptionMethods || [],
        limitOrderTypes:
          voucherType.discountCollectionMethods === null ||
          voucherType.discountCollectionMethods === undefined
            ? false
            : true,
        voucherTypeDiscount: {
          appliesToClassifications: voucherType.discountClassifications || [],
          appliesToCollectionMethods:
            voucherType.discountCollectionMethods || [],
          appliesToProducts: voucherType.discountProducts,
          type: voucherType.discountType,
          value:
            voucherType.discountType === VOUCHER_TYPES.VALUE_ORDER
              ? transformPriceFromAPI(
                  voucherType.discountValue,
                  voucherType.monetaryCurrency
                )
              : voucherType.discountValue,
          useLimit: voucherType.discountUseLimit,
        },
      });
    }
  }, [
    isEdit,
    voucherType,
    reset,
    isLoadingVoucherType,
    isLoadingCountries,
    defaultCurrency,
    countries,
  ]);

  const discountTypeValue = watch("voucherTypeDiscount.type");

  const onSubmit = async (
    data_: VoucherFormType & { limitOrderTypes: boolean }
  ) => {
    const { limitOrderTypes, ...data } = data_;
    if (
      data.voucherTypeDiscount.appliesToProducts &&
      data.voucherTypeDiscount.appliesToProducts.length > 0
    ) {
      data.voucherTypeDiscount.appliesToProducts = _.filter(
        data.voucherTypeDiscount.appliesToProducts,
        (x) => x.id !== ""
      );
    }

    if (!data.expiresAt) {
      data.expiresAt = null;
    }
    if (data.voucherTypeDiscount.type !== VOUCHER_TYPES.PERCENTAGE_PRODUCT) {
      data.voucherTypeDiscount.appliesToProducts = [];
      data.voucherTypeDiscount.useLimit = null;
    } else {
      data.voucherTypeDiscount.useLimit = 1;
    }

    if (data.voucherTypeDiscount.type === VOUCHER_TYPES.VALUE_ORDER) {
      data.voucherTypeDiscount.value = transformPriceInput(
        data.voucherTypeDiscount.value,
        currency
      );
    }

    if (
      data.voucherTypeDiscount.appliesToCollectionMethods &&
      data.voucherTypeDiscount.appliesToCollectionMethods.length === 0
    ) {
      data.voucherTypeDiscount.appliesToCollectionMethods = null;
    }

    try {
      let voucherId = isEdit ? id : null;
      if (isEdit) {
        await updateVoucher({
          data,
          organizationKeyName: organization,
          id,
        });
      } else {
        const voucher = await createVoucher({
          data,
          organizationKeyName: organization,
        });
        voucherId = voucher.id;
      }
      if (!voucherId) {
        throw new Error("No voucher type id found");
      }
      if (image) {
        await uploadVoucherImage({
          file: image,
          organizationKeyName: organization,
          id: voucherId,
        });
      }
      navigate(routes.voucherTypeList(organization));
      toastr.success(
        "Success",
        `Voucher type ${isEdit ? "updated" : "created"}`
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useHandleFetchError(listCountriesError);
  useHandleFetchError(listVoucherTypeError);

  if (
    isEdit ? isLoadingVoucherType || isLoadingCountries : isLoadingCountries
  ) {
    return <SpinnerBlock />;
  }

  const options = [
    {
      label: "Percentage discount on order",
      value: VOUCHER_TYPES.PERCENTAGE_ORDER,
    },
    {
      label: "Percentage discount on product",
      value: VOUCHER_TYPES.PERCENTAGE_PRODUCT,
    },
    {
      label: "Value discount on order",
      value: VOUCHER_TYPES.VALUE_ORDER,
    },
  ];

  return (
    <FormProvider {...formMethods}>
      <div className="container mt-5">
        <GoBackLinkButton
          to={routes.voucherTypeList(organization)}
          text="Back to all voucher templates"
        />
        <div className="row">
          <div className="col-12">
            <form className="card" onSubmit={handleSubmit(onSubmit)}>
              <div className="card-header">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneRegular card-title"
                >
                  {isEdit ? (
                    <>Voucher template edit</>
                  ) : (
                    <>Create voucher template</>
                  )}
                </StyledText>
                {isEdit && (
                  <div className="card-options">
                    <PrimaryButton
                      type="button"
                      color="white"
                      backgroundColor={colors.errorDark}
                      hoverColor={colors.errorMedium}
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `🛑 Are you sure you want to proceed?\n${voucherType?.title} will be deleted and no longer be visible in apps and on web.`
                          )
                        ) {
                          return;
                        }
                        try {
                          await archiveVoucherType({
                            id: id!,
                            organizationKeyName: organization,
                          });
                          navigate(routes.voucherTypeList(organization));
                        } catch (error) {
                          const errorMessage = getErrorMessage(error);
                          toastr.error("Error", errorMessage);
                        }
                      }}
                      disabled={isLoadingArchiveVoucherType}
                    >
                      {isLoadingArchiveVoucherType ? (
                        <ActionSpinner />
                      ) : (
                        "Delete"
                      )}
                    </PrimaryButton>
                  </div>
                )}
              </div>
              <div className="card-body">
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-4"
                  >
                    Voucher template country
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-4"
                  >
                    Choose the country that the voucher template should be used
                    in.
                  </StyledText>
                </div>
                <FormRow>
                  <SelectInput
                    name="countryCode"
                    label="Voucher country"
                    isDisabled={isEdit}
                    options={countryOptions}
                    onChange={() => {
                      replace([]);
                    }}
                  />
                </FormRow>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-4"
                  >
                    Voucher template configuration
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-4"
                  >
                    Choose the type of voucher template you want to create below
                    and the discount value that it should give to the user.
                  </StyledText>
                </div>
                <FormRow className="mb-4">
                  <SelectInput
                    name="voucherTypeDiscount.type"
                    label="Voucher type"
                    options={options}
                    onChange={(v) => {
                      const currentValues =
                        watch("voucherTypeDiscount.appliesToClassifications") ||
                        [];

                      if (
                        v === VOUCHER_TYPES.PERCENTAGE_ORDER ||
                        v === VOUCHER_TYPES.VALUE_ORDER
                      ) {
                        // Persist values, but make sure generic and product additions are always included.
                        setValue(
                          "voucherTypeDiscount.appliesToClassifications",
                          Array.from(
                            new Set([
                              ...currentValues,
                              "generic",
                              "production_addition",
                            ])
                          )
                        );
                        return;
                      }

                      // We hide the other options if the user selects "Percentage product", and therefor should not persist chosen other values.
                      const filteredValues = currentValues.filter(
                        (v) => v === "delivery_buffer"
                      );

                      setValue("voucherTypeDiscount.appliesToClassifications", [
                        ...filteredValues,
                      ]);
                    }}
                  />
                </FormRow>
                <FormRow>
                  {discountTypeValue === "DISCOUNT_TYPE_VALUE_ORDER" ? (
                    <PriceInput
                      formMethods={formMethods}
                      name="voucherTypeDiscount.value"
                      currencyString={currency}
                      label="Discount value"
                      additionalStyles={additionalStylePriceInput}
                      helpertext="Enter the monetary value of the voucher template."
                    />
                  ) : (
                    <Inputs.InputFormField
                      errors={formMethods.formState.errors}
                      register={formMethods.register}
                      type="number"
                      label="Discount value"
                      errorMessage={
                        formErrors.voucherTypeDiscount?.value &&
                        formErrors.voucherTypeDiscount.value?.message
                      }
                      name="voucherTypeDiscount.value"
                      helpText="For “Percentage order” & “Percentage product” = write the percentage discount of the voucher template. Do not write %. E.g. for 25% discount, write 25"
                    />
                  )}
                </FormRow>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-4 mt-2"
                  >
                    Rewards & Challenge
                  </StyledText>
                </div>
                <FormRow>
                  <SwitchInput
                    label="Customer earns points for Challenges and Rewards when making a purchase where voucher is added. Example: If a voucher gives 20% discount on 120 DKK product, the customer pays 96 DKK and will earn points based on the 96 DKK they pay."
                    name="allowChallengeProgress"
                  />
                </FormRow>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-6"
                  >
                    Standard voucher template information
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Enter the standard voucher template information below. These
                    must be entered in order to create the voucher template.
                  </StyledText>
                </div>
                <FormRow>
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Voucher template name"
                    name="title"
                    helpText="Visible in app by users"
                  />
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Admin name"
                    name="name"
                    helpText="Only visible for admins"
                  />
                </FormRow>
                <FormRow>
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Description"
                    name="description"
                    helpText="Write a short description of the voucher template. This will appear right under the name."
                  />
                </FormRow>
                {discountTypeValue === "DISCOUNT_TYPE_PERCENTAGE_PRODUCT" && (
                  <FormRow>
                    <Inputs.InputFormField
                      errors={formMethods.formState.errors}
                      register={formMethods.register}
                      label="OnlinePOS campaign id"
                      name="onlineposCampaignId"
                      helpText="The id of the equivalent campaign in OnlinePOS"
                    />
                  </FormRow>
                )}
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-4"
                  >
                    Voucher template validity
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Enter how long the voucher template is valid for when a user
                    redeems a voucher from a code. E.g. if a voucher template is
                    valid for 30 days, the voucher will be valid for each unique
                    user for 30 days from the day the user receives the voucher.
                  </StyledText>
                </div>
                <FormRow>
                  <Inputs.DurationInputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Duration"
                    name="duration"
                    getValues={getValues}
                    setValue={setValue}
                    watch={watch}
                  />
                </FormRow>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-4"
                  >
                    Voucher template expiry date
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Should your voucher template expire after a certain date?
                    Choose a date below, and the template will expire at 23:59
                    on that date. This means that the date you choose below will
                    be included. Leave empty if not relevant.
                  </StyledText>
                </div>
                <FormRow>
                  <DatePickerInput
                    name="expiresAt"
                    minDate={new Date()}
                    showTimeSelect
                    label="Voucher expiration date"
                  />
                </FormRow>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-6"
                  >
                    Voucher template image
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Upload the image that should appear on the voucher template
                    background.
                  </StyledText>
                </div>
                <Inputs.DropzoneFormField
                  name="image"
                  label="Image"
                  onDrop={(acceptedFiles) => setImage(acceptedFiles?.[0])}
                  selectedFiles={image ? [image] : undefined}
                  onSelectedFileRemove={() => setImage(undefined)}
                  accept=".jpg,.jpeg,.png"
                  maxFiles={1}
                >
                  {voucherType && voucherType.imageUrl && (
                    <div className="row">
                      <div className="col-md-3">
                        <div className="card">
                          <a href={voucherType.imageUrl}>
                            <img
                              src={voucherType.imageUrl}
                              alt={voucherType.imageUrl}
                            />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </Inputs.DropzoneFormField>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-6"
                  >
                    Order types
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-4"
                  >
                    You can limit the voucher template to only work for specific
                    order types. To limit the voucher template to specific order
                    types, check the toggle below and select the appropriate
                    order types. Fx. if you want to limit the voucher template
                    to only work for pick-up orders, select pick-up below. If
                    you want to allow all order types, leave toggle below
                    unchecked.
                  </StyledText>
                </div>
                <FormRow className="mb-4">
                  <SwitchInput
                    label="Limit voucher template to specific order types"
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) {
                        return;
                      }

                      field.onChange([]);
                    }}
                    name="limitOrderTypes"
                  />
                </FormRow>
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-6"
                  >
                    Voucher redemption methods
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-4"
                  >
                    You can choose if the vouchers made from this template can
                    be redeemed via online orders, via swiping through the app
                    in-store or both.
                  </StyledText>
                </div>
                <FormRow className="mb-4">
                  <SelectField
                    isMulti
                    closeMenuOnSelect={false}
                    label="Select ways of redeemding the voucher"
                    placeholder="Start typing to search for redemption methods..."
                    errorMessage={
                      formMethods.getFieldState(
                        "supportedRedemptionMethods",
                        formMethods.formState
                      ).error?.message
                    }
                    onChange={(value) => {
                      const values = value as {
                        value: string;
                        label: string;
                      }[];
                      controllerOnChange(values.map((x) => x.value));
                    }}
                    options={[
                      {
                        value: "order",
                        label: "Online order",
                      },
                      {
                        value: "swipe",
                        label: "In-store swipe",
                      },
                    ]}
                    value={(value || []).map((x) => ({
                      value: x,
                      label: x === "order" ? "Online order" : "In-store swipe",
                    }))}
                    innerRef={ref}
                    {...controllerRest}
                  />
                </FormRow>
                {watch("limitOrderTypes") && (
                  <>
                    {Object.values(CollectionMethod).map((key, index) => (
                      <FormRow className="mb-2" key={index}>
                        <CheckboxField
                          label={getCollectionMethodLabel(key)}
                          value={key}
                          checked={
                            field.value?.includes(key as CollectionMethod) ||
                            false
                          }
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              field.onChange([...(field.value || []), key]);
                            } else {
                              field.onChange(
                                field.value?.filter((v) => v !== key) || []
                              );
                            }
                          }}
                        />
                      </FormRow>
                    ))}
                  </>
                )}

                <>
                  <div>
                    <StyledText
                      color={colors.textPrimary}
                      className="bodyHeaderOneSemi mb-2 mt-6"
                    >
                      Voucher template products classifications
                    </StyledText>
                    <StyledText
                      color={colors.textPrimary}
                      className="bodyTextRegular mb-6"
                    >
                      The types of products the voucher template is eligible
                      for. Check "This voucher template works for all regular
                      products" if the voucher template should work for all
                      generic products, check "This voucher template works for
                      all product additions" if the voucher template should work
                      for all product additions and check both if the voucher
                      template should work for both product types.
                    </StyledText>
                  </div>
                  <FormRow>
                    <Inputs.CheckboxGroupInventoryItemClassificationFormField
                      errors={formMethods.formState.errors}
                      register={formMethods.register}
                      name="voucherTypeDiscount.appliesToClassifications"
                      include={
                        discountTypeValue === VOUCHER_TYPES.PERCENTAGE_PRODUCT
                          ? ["delivery_buffer"]
                          : ["all"]
                      }
                    />
                  </FormRow>
                </>
                {discountTypeValue === VOUCHER_TYPES.PERCENTAGE_PRODUCT && (
                  <>
                    <div className="mt-6">
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyHeaderOneSemi mb-2 mt-6"
                      >
                        Voucher template products
                      </StyledText>
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-6"
                      >
                        Choose the products that this voucher template is
                        eligible for
                      </StyledText>
                    </div>
                    <div css={productsTableWrapperStyle}>
                      {fields.length > 0 && (
                        <div css={tableWrapperStyle}>
                          <Table>
                            <thead>
                              <TableRowHeader borderTop={false}>
                                <TableCellHeaderText className="pl-3">
                                  Product name
                                </TableCellHeaderText>
                                <th />
                              </TableRowHeader>
                            </thead>
                            <TableBody borderBottom={false}>
                              {fields.map((field, i) => {
                                return (
                                  <TableRow key={field.id}>
                                    <TableCellText>
                                      <StyledText
                                        className="bodyTextRegular mb-0"
                                        color={colors.textPrimary}
                                      >
                                        {field.name}
                                      </StyledText>
                                    </TableCellText>
                                    <TableCellText className="pr-3">
                                      <div css={removeProductButtonStyle}>
                                        <IconButton
                                          icon={
                                            <FontAwesomeIcon
                                              icon={solid("xmark")}
                                            />
                                          }
                                          onClick={() => remove(i)}
                                        />
                                      </div>
                                    </TableCellText>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      <SelectProductsSelector
                        filters={{ classification: "admin-main-list" }}
                        filterProductsFunction={(products) => {
                          return products.filter((p) => {
                            const countryCode = watch("countryCode") || "DK";
                            const countryInfo = getCountryInfo(countryCode);
                            if (
                              countryInfo &&
                              countryInfo.currency.length > 0
                            ) {
                              return (
                                countryInfo?.currency[0] === p.price?.currency
                              );
                            }
                            return false;
                          });
                        }}
                        productIdsToLeaveOut={formMethods
                          .watch("voucherTypeDiscount.appliesToProducts")
                          .map((x: any) => x.id)}
                        buttonText="Add products"
                        modalHeader="Add products"
                        onSave={(products) => {
                          const currentProducts = formMethods.getValues(
                            "voucherTypeDiscount.appliesToProducts"
                          );
                          const productsToAdd: VoucherProduct[] = products.map(
                            (p) => ({
                              id: p.id,
                              name: p.name,
                              title: p.title,
                              _type: "",
                            })
                          );
                          replace([...currentProducts, ...productsToAdd]);
                        }}
                      />
                    </div>
                    {/* <SelectField
                      isMulti
                      label="Products"
                      closeMenuOnSelect={false}
                      errorMessage={
                        formMethods.getFieldState(
                          "voucherTypeDiscount.appliesToProducts",
                          formMethods.formState
                        ).error?.message
                      }
                      onChange={(value) => {
                        replace(
                          (value as { value: string; label: string }[]).map(
                            (x) => {
                              const product = allProductsUnsorted?.find(
                                (p) => p.id === x.value
                              );
                              return {
                                id: x.value,
                                name: product?.name || x.label,
                                title: product?.title || x.label,
                                _type: "",
                              };
                            }
                          )
                        );
                      }}
                      options={productOptions}
                      value={productOptions?.filter((option) => {
                        return watch(
                          "voucherTypeDiscount.appliesToProducts"
                        ).find((product) => product.id === option.value);
                      })}
                    /> */}
                  </>
                )}
              </div>
              <div className="card-footer d-flex justify-content-end">
                <ButtonAction disabled={isLoadingMutation} type="submit">
                  {isLoadingMutation ? <ActionSpinner /> : "Save"}
                </ButtonAction>
              </div>
            </form>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default VoucherTypeUpsert;
